/**
 * Declarative schemas for shareable query-string parameters.
 * Defaults are omitted from the canonical URL.
 */

/** @typedef {'string'|'enum'|'int'|'boolean'|'period'|'id'|'list'} ParamType */

/**
 * @typedef {object} ParamSchema
 * @property {ParamType} type
 * @property {*} [defaultValue]
 * @property {string[]} [values] — for enum
 * @property {number} [min]
 * @property {number} [max]
 * @property {number} [maxLength]
 * @property {boolean} [optional]
 */

/** Shared field definitions reused across modules. */
export const COMMON_PARAMS = {
  tenantId: { type: 'id', defaultValue: null, maxLength: 64 },
  ticketId: { type: 'id', defaultValue: null, maxLength: 64 },
  q: { type: 'string', defaultValue: '', maxLength: 200 },
  page: { type: 'int', defaultValue: 1, min: 1, max: 10000 },
  pageSize: { type: 'int', defaultValue: 20, min: 1, max: 100 },
  period: {
    type: 'enum',
    defaultValue: '30d',
    values: ['24h', '7d', '30d', '90d', 'ytd', 'all'],
  },
  sort: { type: 'string', defaultValue: '', maxLength: 64 },
  order: { type: 'enum', defaultValue: 'desc', values: ['asc', 'desc'] },
};

export const AUDITORIA_SEARCH_SCHEMA = {
  tenantId: COMMON_PARAMS.tenantId,
  q: COMMON_PARAMS.q,
  severity: {
    type: 'enum',
    defaultValue: 'all',
    values: ['all', 'info', 'atencao', 'critico', 'seguranca'],
  },
  result: {
    type: 'enum',
    defaultValue: 'all',
    values: ['all', 'success', 'falha', 'parcial'],
  },
  action: { type: 'string', defaultValue: '', maxLength: 80 },
  period: {
    type: 'enum',
    defaultValue: '7d',
    values: ['24h', '7d', '30d', 'all'],
  },
  chip: {
    type: 'enum',
    defaultValue: 'all',
    values: ['all', 'critico', 'seguranca', 'impersonacao', 'repairs', 'falha'],
  },
  page: COMMON_PARAMS.page,
};

export const LOJAS_SEARCH_SCHEMA = {
  q: COMMON_PARAMS.q,
  status: {
    type: 'enum',
    defaultValue: 'all',
    values: [
      'all',
      'active',
      'trial',
      'overdue',
      'suspended',
      'provisioning_failed',
      'archived',
    ],
  },
  plan: { type: 'string', defaultValue: '', maxLength: 40 },
  include_archived: { type: 'boolean', defaultValue: false },
  page: COMMON_PARAMS.page,
};

export const BILLING_SEARCH_SCHEMA = {
  tenantId: COMMON_PARAMS.tenantId,
  q: COMMON_PARAMS.q,
  status: { type: 'string', defaultValue: '', maxLength: 40 },
  page: COMMON_PARAMS.page,
};

export const EQUIPE_SEARCH_SCHEMA = {
  q: COMMON_PARAMS.q,
  role: { type: 'string', defaultValue: '', maxLength: 40 },
  status: {
    type: 'enum',
    defaultValue: 'all',
    values: ['all', 'active', 'pending', 'inactive', 'blocked'],
  },
  page: COMMON_PARAMS.page,
};

export const REPAIRS_SEARCH_SCHEMA = {
  tenantId: COMMON_PARAMS.tenantId,
  ticketId: COMMON_PARAMS.ticketId,
};

export const INTEGRATIONS_SEARCH_SCHEMA = {
  tenantId: COMMON_PARAMS.tenantId,
};

export const OBSERVABILIDADE_SEARCH_SCHEMA = {
  tenantId: COMMON_PARAMS.tenantId,
  period: COMMON_PARAMS.period,
  service: { type: 'string', defaultValue: '', maxLength: 64 },
  status: { type: 'string', defaultValue: '', maxLength: 40 },
  page: COMMON_PARAMS.page,
};

export const SUPPORT_REPORTS_SEARCH_SCHEMA = {
  period: {
    type: 'enum',
    defaultValue: '30d',
    values: ['7d', '30d', '90d', 'ytd'],
  },
  plan: { type: 'string', defaultValue: '', maxLength: 40 },
  tenantId: COMMON_PARAMS.tenantId,
};

export const NOTIFICACOES_SEARCH_SCHEMA = {
  filter: {
    type: 'enum',
    defaultValue: 'all',
    values: [
      'all',
      'unread',
      'billing',
      'tenant',
      'security',
      'system',
      // Mock-only filters (kept for URL compat when USE_MOCK_NOTIFICATIONS)
      'sla_ticket',
      'provisioning',
      'observability',
      'integration',
    ],
  },
};

export const KB_SEARCH_SCHEMA = {
  ticketId: COMMON_PARAMS.ticketId,
  q: COMMON_PARAMS.q,
  status: { type: 'string', defaultValue: '', maxLength: 40 },
  cat: { type: 'string', defaultValue: '', maxLength: 40 },
};

export const PLANOS_PERMISSOES_SEARCH_SCHEMA = {
  tenantId: COMMON_PARAMS.tenantId,
};

export const INADIMPLENCIA_SEARCH_SCHEMA = {
  tenantId: COMMON_PARAMS.tenantId,
  status: { type: 'string', defaultValue: '', maxLength: 40 },
  period: COMMON_PARAMS.period,
  page: COMMON_PARAMS.page,
  q: COMMON_PARAMS.q,
};

/** Map page id → schema (null = no shareable filters). */
export const PAGE_SEARCH_SCHEMAS = {
  lojas: LOJAS_SEARCH_SCHEMA,
  billing: BILLING_SEARCH_SCHEMA,
  inadimplencia: INADIMPLENCIA_SEARCH_SCHEMA,
  auditoria: AUDITORIA_SEARCH_SCHEMA,
  equipe: EQUIPE_SEARCH_SCHEMA,
  integrations: INTEGRATIONS_SEARCH_SCHEMA,
  observabilidade: OBSERVABILIDADE_SEARCH_SCHEMA,
  repairs: REPAIRS_SEARCH_SCHEMA,
  'planos-permissoes': PLANOS_PERMISSOES_SEARCH_SCHEMA,
  'support-reports': SUPPORT_REPORTS_SEARCH_SCHEMA,
  'notificacoes-ops': NOTIFICACOES_SEARCH_SCHEMA,
  'knowledge-base': KB_SEARCH_SCHEMA,
};
